/**
 * Self-typing text for elements marked with [data-self-typer].
 *
 * Optional:
 * - data-self-typer-strings='["Phrase one.", "Phrase two."]'
 * - data-self-typer-prefix="Specialising in " (when prefix is not in element text)
 */
import Typed from 'typed.js';

const DEFAULT_STRINGS = [
  'Web Development.',
  'App Development.',
  'eCommerce.',
  'Digital Strategy.',
  'Online Marketing.',
  'Growing Your Business!',
];

const DEFAULT_OPTIONS = {
  typeSpeed: 50,
  backSpeed: 30,
  backDelay: 1000,
  startDelay: 1500,
  loop: true,
  showCursor: false,
  smartBackspace: false,
};

function parseStrings(el) {
  const raw = el.dataset.selfTyperStrings;
  if (!raw) {
    return DEFAULT_STRINGS;
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_STRINGS;
  } catch {
    return DEFAULT_STRINGS;
  }
}

function extractStaticPrefix(el) {
  if (el.dataset.selfTyperPrefix) {
    return el.dataset.selfTyperPrefix;
  }

  const text = Array.from(el.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent)
    .join('');

  if (!text.trim()) {
    return '';
  }

  return text.endsWith(' ') ? text : `${text.trimEnd()} `;
}

function getTyperRoot(el) {
  if (el.classList.contains('self-typer')) {
    return el;
  }

  return el.querySelector(':scope > .self-typer');
}

function createPrefix(text) {
  const prefix = document.createElement('span');
  prefix.className = 'self-typer__prefix';
  prefix.textContent = text;
  return prefix;
}

function prepareTyperElement(el) {
  let root = getTyperRoot(el);
  let stack = root?.querySelector(':scope > .self-typer__stack');
  let sizer = stack?.querySelector(':scope > .self-typer__sizer');
  let sizerText = sizer?.querySelector(':scope > .self-typer__sizer-text');
  let typed = stack?.querySelector(':scope > .self-typer__typed');
  let prefix = typed?.querySelector(':scope > .self-typer__prefix');
  let target = typed?.querySelector(':scope > .self-typer__typed-text');

  if (!root || !stack || !sizer || !sizerText || !typed || !target) {
    const staticPrefix = extractStaticPrefix(el);

    Array.from(el.childNodes).forEach((node) => node.remove());

    if (staticPrefix) {
      root = document.createElement('span');
      root.className = 'self-typer';
      el.append(root);
    } else {
      root = el;
      root.classList.add('self-typer');
    }

    stack = document.createElement('span');
    stack.className = 'self-typer__stack';

    sizer = document.createElement('span');
    sizer.className = 'self-typer__sizer';
    sizer.setAttribute('aria-hidden', 'true');

    sizerText = document.createElement('span');
    sizerText.className = 'self-typer__sizer-text';

    typed = document.createElement('span');
    typed.className = 'self-typer__typed';

    target = document.createElement('span');
    target.className = 'self-typer__typed-text';

    if (staticPrefix) {
      prefix = createPrefix(staticPrefix);
      sizer.append(createPrefix(staticPrefix), sizerText);
      typed.append(prefix, target);
    } else {
      sizer.append(sizerText);
      typed.append(target);
    }

    stack.append(sizer, typed);
    root.append(stack);
  }

  prefix = typed.querySelector(':scope > .self-typer__prefix');

  return { el, root, prefix, stack, sizer, sizerText, target };
}

function forceReflow(node) {
  void node.offsetHeight;
}

function getContainerWidth(stack) {
  const block = stack.closest('[data-self-typer]') || stack.parentElement;
  return block ? block.getBoundingClientRect().width : 0;
}

function applyContainerWidth(stack) {
  const width = getContainerWidth(stack);

  if (width > 0) {
    stack.style.width = `${width}px`;
  }
}

function measureTextWidth(el, text) {
  el.textContent = text;
  el.style.whiteSpace = 'nowrap';
  forceReflow(el);
  const width = el.getBoundingClientRect().width;
  el.style.whiteSpace = '';

  return width;
}

function measurePrefixWidth(prefix) {
  return prefix ? prefix.getBoundingClientRect().width : 0;
}

function wrapString(stack, sizer, prefix, text) {
  const containerWidth = getContainerWidth(stack);

  if (!containerWidth) {
    return text;
  }

  const sizerText = sizer.querySelector(':scope > .self-typer__sizer-text');
  const tokens = text.match(/\S+\s*/g);

  if (!tokens) {
    return text;
  }

  const prefixWidth = measurePrefixWidth(prefix);
  const lines = [];
  let line = '';
  let isFirstLine = true;

  for (const token of tokens) {
    const candidate = line + token;
    const lineWidth = measureTextWidth(sizerText, candidate);
    const maxWidth = isFirstLine
      ? Math.max(0, containerWidth - prefixWidth)
      : containerWidth;
    const overflows = line && lineWidth > maxWidth;

    if (overflows) {
      lines.push(line.trimEnd());
      line = token;
      isFirstLine = false;
    } else {
      line = candidate;
    }
  }

  if (line) {
    lines.push(line.trimEnd());
  }

  sizerText.textContent = '';

  return lines.join('\n');
}

function measureSizerHeight(sizer, text) {
  const sizerText = sizer.querySelector(':scope > .self-typer__sizer-text');
  if (!sizerText) {
    return 0;
  }

  sizerText.textContent = text;
  forceReflow(sizer);

  return sizer.getBoundingClientRect().height;
}

function updateSizer(sizer, text) {
  const sizerText = sizer.querySelector(':scope > .self-typer__sizer-text');

  if (sizerText) {
    sizerText.textContent = text;
    forceReflow(sizer);
  }
}

function wrapStrings(stack, sizer, prefix, strings) {
  applyContainerWidth(stack);

  return strings.map((str) => wrapString(stack, sizer, prefix, str));
}

function preloadStrings(el, stack, sizer, prefix, strings, activeIndex = 0) {
  applyContainerWidth(stack);

  const wrappedStrings = wrapStrings(stack, sizer, prefix, strings);
  let maxHeight = 0;

  wrappedStrings.forEach((str) => {
    maxHeight = Math.max(maxHeight, measureSizerHeight(sizer, str));
  });

  if (maxHeight > 0) {
    el.style.minHeight = `${Math.ceil(maxHeight)}px`;
  }

  updateSizer(sizer, wrappedStrings[activeIndex] ?? '');
  forceReflow(stack);

  return wrappedStrings;
}

function syncTypedStrings(typed, wrappedStrings) {
  typed.strings = wrappedStrings.map((str) => str.trim());
  typed.sequence = typed.strings.map((_, index) => index);
}

function createTypedInstance(el, stack, sizer, prefix, target, strings, wrappedStrings) {
  const typed = new Typed(target, {
    ...DEFAULT_OPTIONS,
    strings: wrappedStrings,
    preStringTyped(arrayPos) {
      el.dataset.selfTyperCurrent = String(arrayPos);
      updateSizer(sizer, wrappedStrings[arrayPos]);
      target.textContent = '';
    },
    onLastStringBackspaced() {
      target.textContent = '';
      const refreshed = preloadStrings(el, stack, sizer, prefix, strings, 0);
      wrappedStrings.splice(0, wrappedStrings.length, ...refreshed);
      syncTypedStrings(typed, wrappedStrings);
      typed.strPos = 0;
      typed.arrayPos = 0;
    },
  });

  el._selfTyperInstance = typed;

  return typed;
}

function remeasureTyper(el) {
  const root = getTyperRoot(el);
  const stack = root?.querySelector(':scope > .self-typer__stack');
  const sizer = stack?.querySelector(':scope > .self-typer__sizer');
  const prefix = stack?.querySelector(':scope > .self-typer__typed > .self-typer__prefix');
  const typed = el._selfTyperInstance;

  if (!root || !stack || !sizer || !typed) {
    return;
  }

  const strings = parseStrings(el);
  const index = Number(el.dataset.selfTyperCurrent || 0);

  el.style.minHeight = '';
  const wrappedStrings = preloadStrings(el, stack, sizer, prefix, strings, index);
  syncTypedStrings(typed, wrappedStrings);
}

function initTyperElement(el) {
  const strings = parseStrings(el);
  const { stack, sizer, prefix, target } = prepareTyperElement(el);

  el.classList.add('self-typer--loading');
  target.textContent = '';
  el.dataset.selfTyperCurrent = '0';

  const wrappedStrings = preloadStrings(el, stack, sizer, prefix, strings, 0);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      createTypedInstance(el, stack, sizer, prefix, target, strings, wrappedStrings);
      el.classList.remove('self-typer--loading');
      el.classList.add('self-typer--ready');
    });
  });
}

let resizeListenerBound = false;

function bindResizeListener() {
  if (resizeListenerBound) {
    return;
  }

  resizeListenerBound = true;

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document
        .querySelectorAll('[data-self-typer][data-self-typer-init="true"]')
        .forEach(remeasureTyper);
    }, 150);
  });
}

function startTypers() {
  document.querySelectorAll('[data-self-typer]').forEach((el) => {
    if (el.dataset.selfTyperInit === 'true') {
      return;
    }

    el.dataset.selfTyperInit = 'true';
    initTyperElement(el);
  });

  bindResizeListener();
}

export function init_selfTyper() {
  if (!document.querySelector('[data-self-typer]')) {
    return;
  }

  document.fonts.ready.then(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(startTypers);
    });
  });
}
